import React, { createContext, useContext, useState, ReactNode } from 'react';
import { clsx } from 'clsx';
import { cva, VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { Check, XCircle, Info, AlertTriangle } from 'lucide-react';

type ToastVariant = 'success' | 'error' | 'info' | 'warning';

interface Toast {
    id: string;
    variant: ToastVariant;
    description: string;
}

interface ToastContextProps {
    toast: (toast: Omit<Toast, 'id'>) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

// Toast styles
const toastClasses = cva(
    'flex items-center gap-3 px-4 py-2 rounded-md shadow-md transition-all animate-fadeIn z-[9999px] absolute right-[2.4rem] top-[2.4rem] whitespace-nowrap',
    {
        variants: {
            variant: {
                success: 'bg-white',
                error: 'bg-white',
                info: 'bg-white',
                warning: 'bg-white',
            },
        },
        defaultVariants: {
            variant: 'info',
        },
    }
);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = (newToast: Omit<Toast, 'id'>) => {
        setToasts((prev) => [...prev, { ...newToast, id: Date.now().toString() }]);
        setTimeout(() => {
            setToasts((prev) => prev.slice(1));
        }, 2000); // Toast znika po 2 sekundach
    };


    const getIcon = (variant: ToastVariant) => {
        switch (variant) {
            case 'success':
                return <Check color="#22c55e" className="w-2 aspect-square " />;
            case 'error':
                return <XCircle color="#ef4444" className="w-2 aspect-square " />;
            case 'info':
                return <Info color="#dbeafe" className="w-2 aspect-square " />;
            case 'warning':
                return <AlertTriangle color="#f97316" className="w-2 aspect-square " />;
            default:
                return null;
        }
    };

    return (
        <ToastContext.Provider value={{ toast: addToast }}>
            {children}
            <div className="fixed top-4 right-4 space-y-2">
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        className={twMerge(clsx(toastClasses({ variant: toast.variant })))}
                    >
                        {getIcon(toast.variant)}
                        <span>{toast.description}</span>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};
